import { Injectable, effect, inject, signal } from '@angular/core';
import { Subscription, interval } from 'rxjs';
import { KlidiApi } from './api.service';
import { MerchantContextService } from './merchant-context.service';
import { NotificationDto } from '../models/api.models';

/** How often the header re-checks the unread count. */
const POLL_INTERVAL_MS = 60_000;

/**
 * The notification feed behind the header bell.
 *
 * Polls the unread count rather than opening a socket: one indexed COUNT a minute is cheap, and
 * a socket would need reconnection handling and server-side fan-out for a badge that nobody is
 * watching second-by-second. Push covers the case where the app isn't open at all.
 */
@Injectable({ providedIn: 'root' })
export class NotificationService {
  private readonly api = inject(KlidiApi);
  private readonly context = inject(MerchantContextService);

  readonly items = signal<NotificationDto[]>([]);
  readonly unreadCount = signal(0);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);

  private poll?: Subscription;

  constructor() {
    effect(() => {
      const merchantId = this.context.activeMerchantId();

      this.poll?.unsubscribe();
      this.poll = undefined;

      if (merchantId === null) {
        // Signed out, or a business account with nothing to act as — clear rather than
        // leave the previous account's notifications on screen.
        this.items.set([]);
        this.unreadCount.set(0);
        return;
      }

      this.refreshCount(merchantId);
      this.poll = interval(POLL_INTERVAL_MS).subscribe(() => this.refreshCount(merchantId));
    }, { allowSignalWrites: true });
  }

  /** Badge only — cheap, and all the header needs until the menu is opened. */
  private refreshCount(merchantId: number): void {
    this.api.unreadNotificationCount(merchantId).subscribe({
      next: (r) => this.unreadCount.set(r.unreadCount),
      // A failed poll is not worth telling anyone about; the next tick will retry.
      error: () => {}
    });
  }

  /** Full feed. Called when the bell menu opens, so the list is current rather than cached. */
  load(take = 20): void {
    const merchantId = this.context.activeMerchantId();
    if (merchantId === null) return;

    this.loading.set(true);
    this.error.set(null);
    this.api.notifications(merchantId, take).subscribe({
      next: (feed) => {
        this.loading.set(false);
        this.items.set(feed.items);
        this.unreadCount.set(feed.unreadCount);
      },
      error: () => {
        this.loading.set(false);
        this.error.set('Could not load notifications.');
      }
    });
  }

  /** Optimistic: the badge should drop the instant it's tapped, not a round-trip later. */
  markRead(notification: NotificationDto): void {
    if (!notification.unread) return;

    this.items.update(rows =>
      rows.map(r => r.id === notification.id ? { ...r, unread: false } : r));
    this.unreadCount.update(n => Math.max(0, n - 1));

    this.api.markNotificationRead(notification.id).subscribe({
      error: () => {
        // Put it back rather than leave the UI claiming something it failed to do.
        this.items.update(rows =>
          rows.map(r => r.id === notification.id ? { ...r, unread: true } : r));
        this.unreadCount.update(n => n + 1);
      }
    });
  }

  markAllRead(): void {
    const merchantId = this.context.activeMerchantId();
    if (merchantId === null || this.unreadCount() === 0) return;

    const previous = this.items();
    const previousCount = this.unreadCount();

    this.items.update(rows => rows.map(r => ({ ...r, unread: false })));
    this.unreadCount.set(0);

    this.api.markAllNotificationsRead(merchantId).subscribe({
      error: () => {
        this.items.set(previous);
        this.unreadCount.set(previousCount);
      }
    });
  }
}
